import {
  DragDropProvider,
  type DragEndEvent,
  type DragOverEvent,
} from "@dnd-kit/react";
import { useSortable } from "@dnd-kit/react/sortable";
import { GripVertical } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";

import type { AgendaItem } from "@elation/api-client/src/stitch-web";

import {
  ADDED_BY_YOU_KEY,
  AgendaItemEntity,
  ensureDroppableAddedByYouGroup,
  groupByConcern,
  toAgendaConcernHeader,
} from "@src/features/agenda/domain";
import { Button } from "@src/shared/components/ui/button";
import { cn } from "@src/shared/lib/utils";

import { AgendaConcernHeader } from "../AgendaConcernHeader";
import {
  AGENDA_ITEM_DRAG_TYPE,
  type AgendaGroupedDragResolution,
  CONCERN_GROUP_DRAG_TYPE,
  resolveAgendaGroupedDragEnd,
  toAgendaGroupedDragSource,
} from "./resolveAgendaGroupedDragEnd";

/**
 * STITCH-550 — flat DOM, not nested-per-group.
 *
 * This list renders every concern group's header and items as siblings of
 * ONE `<ul>`, instead of a `<ul>` per concern group nested inside a parent
 * `<li>`. Earlier revisions used the nested shape and blocked custom items
 * from crossing concern groups: React had to destroy the dragged `<li>` in
 * one group's `<ul>` and recreate it in another's, which raced dnd-kit's own
 * DOM-node ownership during the drop. A time-boxed spike tried deferring the
 * state update until dnd-kit's drop animation fully settled (its `status`
 * leaving `'dropping'`, not just one animation frame) — that closed the
 * originally-reported `NotFoundError: removeChild` crash, but a real-browser
 * regression test (`TestAllowsCustomItemCrossGroupDrag`) still failed
 * consistently: dnd-kit left an orphaned clone/placeholder `<li>` behind in
 * the destination group even after `status` reported settled, so the DOM
 * showed the moved item twice. That confirmed the problem is structural
 * (a cross-parent DOM transplant), not timing — no amount of waiting fixes a
 * library reparenting a node it still thinks it owns.
 *
 * The fix instead removes the transplant: with one flat `<ul>`, a cross-
 * group move is a reorder among siblings of the SAME parent — the exact
 * scenario `@dnd-kit/react`'s sortable is designed for — plus a data change
 * (`healthConcernId`) applied by the caller. `group` and `index` passed to
 * `useSortable` are logical partitioning for dnd-kit's own collision/index
 * bookkeeping, not a requirement that same-group elements share a DOM
 * parent; that's what makes flattening safe without changing
 * `resolveAgendaGroupedDragEnd`'s index semantics (still per-group-local).
 *
 * Accessibility: headers stay `<li role="listitem">` (the implicit role),
 * each containing an `<h2>`-equivalent heading, rather than becoming
 * `role="separator"`/`role="presentation"` children of the list. A `<ul>`
 * whose children are a mix of `<li>` and bare headings (or non-listitem
 * roles) is invalid list structure for assistive tech; keeping every direct
 * child a real `<li>` preserves valid list semantics for the *whole* list
 * (screen readers still announce "list, N items" and item-by-item
 * navigation works), while the heading inside a header `<li>` still gives
 * heading-jump navigation (H key) between concern sections — the same
 * pattern already used for grouped lists with visible section titles.
 */
type ItemDragData = {
  groupKey: string;
  isOpportunityLinked: boolean;
};

export interface AgendaGroupedSortableListProps {
  items: AgendaItem[];
  disabled?: boolean;
  /**
   * Accessible explanation when reordering is temporarily disabled
   * (for example while optimistic creates are unresolved).
   */
  reorderDisabledReason?: string;
  /**
   * `concernChange` is non-null only when a custom item just crossed into a
   * different concern group (see `AgendaGroupedDragResolution`). The caller
   * MUST persist it (a `healthConcernId` PATCH) — `items` already reflects
   * the moved item's new concern locally, but that's a client-side
   * projection only. If the caller schedules a plain position reorder
   * without also persisting the concern change, the next server refetch
   * reverts the item's concern while keeping its new position, which
   * relocates the "Added by you" header instead of the item (it's anchored
   * to the array position of its first member — see `groupByConcern`).
   */
  onItemsChange: (
    items: AgendaItem[],
    concernChange: AgendaGroupedDragResolution["concernChange"],
  ) => void;
  renderItem: (item: AgendaItem) => ReactNode;
  className?: string;
}

function SortableGroupHeader({
  groupKey,
  index,
  disabled,
  header,
}: {
  groupKey: string;
  index: number;
  disabled: boolean;
  header: ReturnType<typeof toAgendaConcernHeader>;
}) {
  const { ref, handleRef, isDragging, isDropTarget } = useSortable({
    id: `group:${groupKey}`,
    index,
    type: CONCERN_GROUP_DRAG_TYPE,
    accept: CONCERN_GROUP_DRAG_TYPE,
    disabled,
  });

  return (
    <li
      ref={ref}
      className={cn(
        // The group separator itself lives on the LAST ITEM row of each
        // group (see SortableAgendaItem's `isLastInGroup`/`isLastGroup`
        // handling), not here — headers never carry a border. Drawing it on
        // the header would land it between the header and its first item
        // instead of between one group and the next.
        "list-none",
        isDragging ? "opacity-60" : null,
        isDropTarget ? "bg-muted/30" : null,
      )}
      data-dragging={isDragging ? "true" : undefined}
    >
      <AgendaConcernHeader
        header={header}
        className="group/handle"
        dragHandle={
          <Button
            aria-label={`Reorder ${header.title}`}
            className={cn(
              "absolute top-1/2 -left-5 z-10 h-7 w-3.5 -translate-y-1/2 shrink-0 cursor-grab text-muted-foreground/60 opacity-0 transition-opacity duration-150 ease-out pointer-events-none hover:bg-transparent hover:text-foreground active:cursor-grabbing group-hover/handle:pointer-events-auto group-hover/handle:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100 motion-reduce:pointer-events-auto motion-reduce:opacity-100 [&_svg]:size-3.5",
              isDragging
                ? "pointer-events-auto text-foreground opacity-100"
                : null,
              disabled ? "cursor-not-allowed" : null,
            )}
            disabled={disabled}
            ref={(element) => {
              handleRef(element);
            }}
            size="icon"
            type="button"
            variant="ghost"
          >
            <GripVertical aria-hidden="true" />
          </Button>
        }
      />
    </li>
  );
}

/**
 * Drop-only placeholder so an empty Added by you section remains a valid
 * cross-group target after its last member leaves.
 */
function EmptyGroupDropZone({
  groupKey,
  disabled,
}: {
  groupKey: string;
  disabled: boolean;
}) {
  const { ref, isDropTarget } = useSortable({
    id: `empty:${groupKey}`,
    index: 0,
    group: groupKey,
    type: AGENDA_ITEM_DRAG_TYPE,
    accept: AGENDA_ITEM_DRAG_TYPE,
    // Still accept drops; never act as a drag source.
    disabled: disabled ? true : { draggable: true },
    data: {
      groupKey,
      isOpportunityLinked: false,
    } satisfies ItemDragData,
  });

  return (
    <li
      ref={ref}
      aria-label="Drop here to move to Added by you"
      className={cn(
        "list-none min-h-8 pl-6",
        isDropTarget ? "bg-muted/40" : null,
      )}
    />
  );
}

function SortableAgendaItem({
  item,
  index,
  groupKey,
  disabled,
  isLastInGroup,
  isLastGroup,
  children,
}: {
  item: AgendaItem;
  index: number;
  groupKey: string;
  disabled: boolean;
  isLastInGroup: boolean;
  isLastGroup: boolean;
  children: ReactNode;
}) {
  const { ref, handleRef, isDragging, isDropTarget } = useSortable({
    id: item.id,
    index,
    group: groupKey,
    type: AGENDA_ITEM_DRAG_TYPE,
    data: {
      groupKey,
      isOpportunityLinked: item.careProtocolOpportunityId != null,
    } satisfies ItemDragData,
    // Accept by type only. Same-group enforcement lives in onDragOver +
    // resolveAgendaGroupedDragEnd so pointer drops are not blocked when
    // source.data is unavailable during accept evaluation.
    accept: AGENDA_ITEM_DRAG_TYPE,
    disabled,
  });

  // Two separate dividers, deliberately on two different elements, to match
  // the FULL-WIDTH-vs-INSET geometry the pre-flattening nested DOM gave for
  // free:
  //  - The solid GROUP separator used to live on the group `<li>` that
  //    wrapped the header plus its items — an element that sat OUTSIDE the
  //    items' `pl-6` indent — so it drew flush with the header's left edge,
  //    full width. It belongs on this item's own `<li>` (also `pl-6`-padded,
  //    but a border is drawn at an element's own border-box edge regardless
  //    of ITS OWN padding, so a border here is full width too), only on the
  //    last row of a non-final group.
  //  - The dashed ROW separator used to live on each item `<li>` INSIDE a
  //    per-group `<ul className="pl-6">` — inset because it sat inside that
  //    wrapper's padded content box. The inner wrapper below reproduces that
  //    same parent/child relationship (it has no padding of its own; it
  //    simply fills this `<li>`'s already-indented content box), so a border
  //    on IT starts at the indent, not at x=0.
  const isGroupDivider = isLastInGroup && !isLastGroup;
  const isRowDivider = !isLastInGroup;

  return (
    <li
      ref={ref}
      aria-label={AgendaItemEntity.listItemLabel(item) ?? item.description}
      className={cn(
        // pl-6 indents this row's content relative to its group header — the
        // flat list has no wrapping nested <ul> to inherit that indent from
        // (see the module doc comment). Other code (Agenda.stories.tsx)
        // reads this element's own computed `padding-left` to assert the
        // indent, and dnd-kit measures this same element's rect for
        // drag/drop hit-testing — padding doesn't change that rect's
        // position or size, so both are safe here.
        "group/handle pl-6",
        isGroupDivider ? "border-border border-b" : null,
        isDragging ? "opacity-60" : null,
        isDropTarget ? "bg-muted/40" : null,
        disabled ? "opacity-60" : null,
      )}
      data-dragging={isDragging ? "true" : undefined}
    >
      <div
        className={cn(
          "relative flex min-h-8 items-center",
          isRowDivider ? "border-border border-b border-dashed" : null,
        )}
      >
        <Button
          aria-label={`Reorder ${item.description}`}
          className={cn(
            "absolute top-1/2 -left-5 z-10 h-7 w-3.5 -translate-y-1/2 shrink-0 cursor-grab text-muted-foreground/60 opacity-0 transition-opacity duration-150 ease-out pointer-events-none hover:bg-transparent hover:text-foreground active:cursor-grabbing group-hover/handle:pointer-events-auto group-hover/handle:opacity-100 focus-visible:pointer-events-auto focus-visible:opacity-100 [@media(hover:none)]:pointer-events-auto [@media(hover:none)]:opacity-100 motion-reduce:pointer-events-auto motion-reduce:opacity-100 [&_svg]:size-3.5",
            isDragging
              ? "pointer-events-auto text-foreground opacity-100"
              : null,
            disabled ? "cursor-not-allowed" : null,
          )}
          disabled={disabled}
          ref={(element) => {
            handleRef(element);
          }}
          size="icon"
          type="button"
          variant="ghost"
        >
          <GripVertical aria-hidden="true" />
        </Button>
        <div className="min-w-0 flex-1">{children}</div>
      </div>
    </li>
  );
}

export function AgendaGroupedSortableList({
  items,
  disabled = false,
  reorderDisabledReason,
  onItemsChange,
  renderItem,
  className,
}: AgendaGroupedSortableListProps) {
  const groups = useMemo(
    () => ensureDroppableAddedByYouGroup(groupByConcern(items), items),
    [items],
  );

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { source, target } = event.operation;
    if (!source || !target) return;
    if (
      source.type !== AGENDA_ITEM_DRAG_TYPE ||
      target.type !== AGENDA_ITEM_DRAG_TYPE
    ) {
      return;
    }

    const sourceData = source.data as ItemDragData | undefined;
    const targetGroup = (target.data as ItemDragData | undefined)?.groupKey;
    if (sourceData?.groupKey == null || targetGroup == null) return;
    // Opportunity-linked items may never change concern (server rejects a
    // direct healthConcernId write for them) — refuse the cross-group
    // projection at dragover so the pointer drop never even reaches
    // resolveAgendaGroupedDragEnd. Custom items are allowed to project into
    // a different group; see resolveAgendaGroupedDragEnd for the
    // corresponding dragend-time refusal (belt and suspenders).
    if (sourceData.groupKey !== targetGroup && sourceData.isOpportunityLinked) {
      event.preventDefault();
    }
  }, []);

  // STITCH-550: this list renders every concern group's items (and the
  // group headers) as siblings of one flat <ul> — see the module doc
  // comment for why. A cross-group move is therefore a plain reorder among
  // siblings under the same parent, so no deferral is needed here: dnd-kit
  // owns the dragged node's DOM identity for the whole gesture, and React
  // never has to destroy/recreate it in a different parent.
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (event.canceled || disabled) return;

      const resolution = resolveAgendaGroupedDragEnd(
        items,
        toAgendaGroupedDragSource(event.operation.source),
      );
      if (!resolution) return;

      onItemsChange(resolution.items, resolution.concernChange);
    },
    [disabled, items, onItemsChange],
  );

  const disabledReasonId = "agenda-reorder-disabled-reason";

  return (
    <DragDropProvider onDragEnd={handleDragEnd} onDragOver={handleDragOver}>
      {disabled && reorderDisabledReason ? (
        <p className="sr-only" id={disabledReasonId}>
          {reorderDisabledReason}
        </p>
      ) : null}
      <ul
        aria-describedby={
          disabled && reorderDisabledReason ? disabledReasonId : undefined
        }
        aria-disabled={disabled ? true : undefined}
        aria-label="Visit agenda items"
        className={cn("m-0 w-full list-none p-0", className)}
      >
        {groups.flatMap((group, groupIndex) => {
          const header = toAgendaConcernHeader(group);
          const lastItemIndex = group.items.length - 1;
          const isLastGroup = groupIndex === groups.length - 1;
          const isEmptyAddedByYou =
            group.key === ADDED_BY_YOU_KEY && group.items.length === 0;
          return [
            <SortableGroupHeader
              key={`group:${group.key}`}
              // Empty shell has nothing to reorder among other groups.
              disabled={disabled || isEmptyAddedByYou}
              groupKey={group.key}
              header={header}
              index={groupIndex}
            />,
            ...(isEmptyAddedByYou
              ? [
                  <EmptyGroupDropZone
                    key={`empty:${group.key}`}
                    disabled={disabled}
                    groupKey={group.key}
                  />,
                ]
              : group.items.map((item, itemIndex) => (
                  <SortableAgendaItem
                    key={item.id}
                    disabled={disabled}
                    groupKey={group.key}
                    index={itemIndex}
                    isLastGroup={isLastGroup}
                    isLastInGroup={itemIndex === lastItemIndex}
                    item={item}
                  >
                    {renderItem(item)}
                  </SortableAgendaItem>
                ))),
          ];
        })}
      </ul>
    </DragDropProvider>
  );
}
